import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  Calculator,
  FileText,
  Pencil,
  Power,
  PowerOff,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  PageHeaderSkeleton,
  StatCardsSkeleton,
} from "~/components/ui/skeletons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  useAccount,
  useToggleAccountActive,
  useUpdateAccount,
} from "~/hooks/contabilidad/useAccounts";
import { cn } from "~/lib/utils";
import type { AccountFormData } from "~/types/contabilidad";
import { ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS } from "~/types/contabilidad";

import { CuentaDialog } from "./components/CuentaDialog";

export default function CuentaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: account, isLoading } = useAccount(id!);
  const updateMutation = useUpdateAccount();
  const toggleActiveMutation = useToggleAccountActive();

  const handleUpdate = (data: AccountFormData) => {
    updateMutation.mutate(
      { id: id!, data },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
        },
      }
    );
  };

  const handleToggleActive = () => {
    if (account) {
      toggleActiveMutation.mutate({ id: id!, isActive: !account.is_active });
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <StatCardsSkeleton
          count={3}
          className="sm:grid-cols-3 lg:grid-cols-3"
        />
      </PageContainer>
    );
  }

  if (!account) {
    return (
      <PageContainer>
        <p className="text-muted-foreground">Cuenta no encontrada.</p>
        <Button
          variant="link"
          onClick={() => navigate("/contabilidad/cuentas")}
          className="mt-4"
        >
          Volver a la lista
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`${account.code} - ${account.name}`}
        description={`Creada el ${dayjs(account.created_at).format("DD/MM/YYYY")}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/contabilidad/cuentas")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={handleToggleActive}
              className="h-9"
            >
              {account.is_active ? (
                <>
                  <PowerOff className="mr-2 h-4 w-4" />
                  Desactivar
                </>
              ) : (
                <>
                  <Power className="mr-2 h-4 w-4" />
                  Activar
                </>
              )}
            </Button>
            <Button onClick={() => setEditDialogOpen(true)} className="h-9">
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          </>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tipo de Cuenta
            </CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge
              variant="outline"
              className={cn(
                "text-lg",
                ACCOUNT_TYPE_COLORS[account.account_type]
              )}
            >
              {ACCOUNT_TYPE_LABELS[account.account_type]}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estado</CardTitle>
            {account.is_active ? (
              <Power className="h-4 w-4 text-green-500" />
            ) : (
              <PowerOff className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            <Badge variant={account.is_active ? "default" : "secondary"}>
              {account.is_active ? "Activa" : "Inactiva"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Balance</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$0.00</div>
            <p className="text-xs text-muted-foreground">
              Calculado de movimientos publicados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="movimientos">Movimientos</TabsTrigger>
          <TabsTrigger value="balance">Balance</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Código</p>
                  <p className="font-mono font-medium">{account.code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nombre</p>
                  <p className="font-medium">{account.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Tipo de Cuenta
                  </p>
                  <Badge
                    variant="outline"
                    className={cn(ACCOUNT_TYPE_COLORS[account.account_type])}
                  >
                    {ACCOUNT_TYPE_LABELS[account.account_type]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge variant={account.is_active ? "default" : "secondary"}>
                    {account.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Empresa</p>
                  <p
                    className="cursor-pointer font-medium text-primary hover:underline"
                    onClick={() =>
                      navigate(`/contabilidad/empresas/${account.company_id}`)
                    }
                  >
                    {account.company?.name ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Fecha de Creación
                  </p>
                  <p className="font-medium">
                    {dayjs(account.created_at).format("DD/MM/YYYY HH:mm")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimientos">
          <Card>
            <CardHeader>
              <CardTitle>Movimientos de la Cuenta</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Los movimientos que afectan esta cuenta aparecerán aquí.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="balance">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                El historial de balance de la cuenta aparecerá aquí.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <CuentaDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        account={account}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />
    </PageContainer>
  );
}
